import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axiosInstance from "../../../services/AxiosInstance";
import Api from "../../../constants/Api";
import { successAlert } from "../alert/defaultAlert";
import InputNumeric from "../Forms/Element/InputNumeric";
import YesNoSelect from "../Common/YesNoSelect";
import { convertBooleanToYesOrNo, convertYesOrNoToBoolean } from "../../../utils/ConverterUtils";
import HintTooltip from "../Common/HintTooltip";
import { resolveSingleParameter } from "../../../utils/ParameterUtils";
import "./AppointmentSetting.css";

// Teto da janela do agendamento online; espelha ParameterAppointment#MAX_PUBLIC_BOOKING_DAYS.
const PUBLIC_BOOKING_DAYS_MAX = 90;

const AppointmentSetting = () => {
    const { t } = useTranslation();
    const [blockHoursAfterPenalty, setBlockHoursAfterPenalty] = useState(0);
    const [lateCancellationThresholdHours, setLateCancellationThresholdHours] = useState(0);
    const [maxFutureAppointmentsPerCustomer, setMaxFutureAppointmentsPerCustomer] = useState(1);
    const [publicBookingDays, setPublicBookingDays] = useState(14);
    const [employeeCanSchedule, setEmployeeCanSchedule] = useState(null);
    const [employeeCanViewCustomerContact, setEmployeeCanViewCustomerContact] = useState(null);
    // Espelha o padrão da entidade para uma configuração ainda não cadastrada.
    const [expressSale, setExpressSale] = useState({ value: "Y", label: t('yes') });
    const [sendReviewInvite, setSendReviewInvite] = useState(null);
    const [employeeCanBlockAgenda, setEmployeeCanBlockAgenda] = useState(null);

    // Converte o booleano vindo do servidor na opção do select, ou null quando o campo não veio.
    const toYesNoOption = (value) => {
        if (value === undefined || value === null) {
            return null;
        }

        const yesOrNo = convertBooleanToYesOrNo(value);
        return { value: yesOrNo, label: yesOrNo === "Y" ? t('yes') : t('no') };
    };

    // Campo sem escolha carregada fica fora do payload: o padrão de cada parâmetro é o valor
    // declarado na entidade ParameterAppointment, que é a fonte única desses padrões. Mandar
    // um palpite daqui faria a tela decidir o padrão por conta própria, e as duas pontas
    // divergiriam na primeira vez que o padrão mudasse no servidor.
    const addWhenChosen = (data, field, option) => {
        if (option) {
            data[field] = convertYesOrNoToBoolean(option.value);
        }
    };

    const save = () => {
        const penaltyHours = parseInt(blockHoursAfterPenalty, 10);
        const thresholdHours = parseInt(lateCancellationThresholdHours, 10);
        const futureAppointmentsLimit = parseInt(maxFutureAppointmentsPerCustomer, 10);
        const bookingDays = parseInt(publicBookingDays, 10);
        const data = {
            blockHoursAfterPenalty: isNaN(penaltyHours) ? 0 : penaltyHours,
            // Vazio vale zero, o mesmo padrão do banco e do backend: cancelar não penaliza.
            lateCancellationThresholdHours: isNaN(thresholdHours) ? 0 : thresholdHours,
            maxFutureAppointmentsPerCustomer: isNaN(futureAppointmentsLimit) ? 1 : Math.max(1, futureAppointmentsLimit)
        };

        // Campo vazio fica fora do payload e mantém o valor gravado. Preenchido, respeita a
        // mesma faixa da entidade (1 a 90 dias), para a gravação não ser recusada.
        if (!isNaN(bookingDays)) {
            data.publicBookingDays = Math.min(PUBLIC_BOOKING_DAYS_MAX, Math.max(1, bookingDays));
        }

        addWhenChosen(data, 'employeeCanSchedule', employeeCanSchedule);
        addWhenChosen(data, 'employeeCanViewCustomerContact', employeeCanViewCustomerContact);
        addWhenChosen(data, 'expressSale', expressSale);
        addWhenChosen(data, 'sendReviewInvite', sendReviewInvite);
        addWhenChosen(data, 'employeeCanBlockAgenda', employeeCanBlockAgenda);

        axiosInstance.post(Api.parameter.appointment, data)
            .then(() => successAlert(t('message.data_updated_successfully'), null));
    };

    useEffect(() => {
        axiosInstance.get(Api.parameter.appointment)
            .then((resp) => {
                // Mesma resolução usada pela tela de operação (AppointmentDetailsModal): sem ela,
                // com mais de um registro de parâmetro, uma tela podia salvar num e a outra ler
                // outro, e a configuração parecia não ter sido respeitada.
                const data = resolveSingleParameter(resp.data);
                if (data) {
                    setBlockHoursAfterPenalty(data.blockHoursAfterPenalty !== undefined ? data.blockHoursAfterPenalty : 0);
                    setLateCancellationThresholdHours(data.lateCancellationThresholdHours !== undefined ? data.lateCancellationThresholdHours : 0);
                    setMaxFutureAppointmentsPerCustomer(data.maxFutureAppointmentsPerCustomer !== undefined ? data.maxFutureAppointmentsPerCustomer : 1);
                    if (data.publicBookingDays !== undefined && data.publicBookingDays !== null) {
                        setPublicBookingDays(data.publicBookingDays);
                    }

                    // Os campos ausentes ficam sem escolha, exceto venda rápida, cujo padrão
                    // ativo também é exibido antes de existir uma configuração cadastrada.
                    setEmployeeCanSchedule(toYesNoOption(data.employeeCanSchedule));
                    setEmployeeCanViewCustomerContact(toYesNoOption(data.employeeCanViewCustomerContact));
                    setExpressSale(toYesNoOption(data.expressSale) ?? { value: "Y", label: t('yes') });
                    setSendReviewInvite(toYesNoOption(data.sendReviewInvite));
                    setEmployeeCanBlockAgenda(toYesNoOption(data.employeeCanBlockAgenda));
                }
            });
    }, []);

    return (
        <div className="card">
            <div className="card-body">
                <h3 className="mb-2">{t('appointments')}</h3>
                <p className="text-muted small mb-4">Configure as regras do agendamento, as permissões da equipe e o fluxo de atendimento.</p>

                {/* A separação por contexto reduz a chance de uma permissão ser confundida
                    com uma regra aplicada ao cliente no agendamento público. */}
                <div className="appointment-setting-section mb-4">
                    <div className="appointment-setting-section__header">
                        <h5 className="mb-1">Regras do agendamento online</h5>
                        <p className="text-muted small mb-0">Defina as penalidades por falta ou cancelamento próximo do horário, até quando a agenda fica liberada e quantos agendamentos futuros cada cliente pode manter pelo link público.</p>
                    </div>
                    <div className="row pt-3">
                        <div className="col-lg-6">
                            <div className="setting-input">
                                <label className="form-label">Bloqueio por penalidade (horas) <HintTooltip text="Tempo sem poder agendar online após uma falta ou um cancelamento do cliente abaixo do prazo ao lado. Com 0, ninguém é bloqueado." /></label>
                                <InputNumeric name="blockHoursAfterPenalty" onChange={(e) => setBlockHoursAfterPenalty(e.target.value)} value={blockHoursAfterPenalty} />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="setting-input">
                                <label className="form-label">Prazo para cancelar sem penalidade (horas) <HintTooltip text="Cancelamentos feitos pelo próprio cliente com menos antecedência que este prazo geram bloqueio. Com 0, cancelar nunca bloqueia; só a falta. Cancelamentos feitos pela equipe e remarcações pelo WhatsApp não contam. Não impede o cancelamento." /></label>
                                <InputNumeric name="lateCancellationThresholdHours" onChange={(e) => setLateCancellationThresholdHours(e.target.value)} value={lateCancellationThresholdHours} />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="setting-input">
                                <label className="form-label">Agendamentos futuros (quantidade) <HintTooltip text="Quantidade de horários futuros que cada cliente pode manter pelo agendamento online. Cada plano pode ter um limite próprio para os seus assinantes, no cadastro do plano." /></label>
                                <InputNumeric name="maxFutureAppointmentsPerCustomer" onChange={(e) => setMaxFutureAppointmentsPerCustomer(e.target.value)} value={maxFutureAppointmentsPerCustomer} />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="setting-input">
                                <label className="form-label">Agenda online liberada para (dias) <HintTooltip text="Quantos dias o calendário do agendamento online mostra, contando hoje. Com 14, o cliente marca de hoje até daqui a 13 dias. Máximo de 90. A recepção não é limitada por este campo." /></label>
                                <InputNumeric name="publicBookingDays" onChange={(e) => setPublicBookingDays(e.target.value)} value={publicBookingDays} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="appointment-setting-section mb-4">
                    <div className="appointment-setting-section__header">
                        <h5 className="mb-1">Permissões da equipe</h5>
                        <p className="text-muted small mb-0">Defina o que os colaboradores podem fazer e consultar.</p>
                    </div>
                    <div className="row pt-3">
                        <div className="col-lg-6">
                            <div className="setting-input">
                                <label className="form-label">{t('employee_can_schedule_label')} <HintTooltip text={t('employee_can_schedule_hint')} /></label>
                                <YesNoSelect name="employeeCanSchedule" onChange={(value) => setEmployeeCanSchedule(value)} value={employeeCanSchedule} />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="setting-input">
                                <label className="form-label">{t('employee_can_view_customer_contact_label')} <HintTooltip text={t('employee_can_view_customer_contact_short_hint')} /></label>
                                <YesNoSelect name="employeeCanViewCustomerContact" onChange={(value) => setEmployeeCanViewCustomerContact(value)} value={employeeCanViewCustomerContact} />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="setting-input">
                                <label className="form-label">{t('employee_can_block_agenda_label')} <HintTooltip text={t('employee_can_block_agenda_hint')} /></label>
                                <YesNoSelect name="employeeCanBlockAgenda" onChange={(value) => setEmployeeCanBlockAgenda(value)} value={employeeCanBlockAgenda} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="appointment-setting-section mb-4">
                    <div className="appointment-setting-section__header">
                        <h5 className="mb-1">Fluxo de atendimento</h5>
                        <p className="text-muted small mb-0">Configurações usadas durante a conclusão do atendimento.</p>
                    </div>
                    <div className="row pt-3">
                        <div className="col-lg-6">
                            <div className="setting-input">
                                <label className="form-label">{t('express_sale_label')} <HintTooltip text={t('express_sale_short_hint')} /></label>
                                <YesNoSelect name="expressSale" onChange={(value) => setExpressSale(value)} value={expressSale} />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="setting-input">
                                <label className="form-label">{t('send_review_invite_label')} <HintTooltip text={t('send_review_invite_short_hint')} /></label>
                                <YesNoSelect name="sendReviewInvite" onChange={(value) => setSendReviewInvite(value)} value={sendReviewInvite} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-xl-12">
                        <Link to={"#"} className="btn btn-primary btn-md" onClick={() => save()}>
                            {t('save_setting')}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentSetting;
